import { useEffect, useRef } from "react";
import { Receipt, X } from "lucide-react";
import type { PayBreakdownLine } from "@crewledger/shared/schemas";
import { Button } from "@/components/ui/button";
import { FormHint } from "@/components/design";
import { formatCurrency } from "@/features/dashboard/utils";
import { MICROCOPY } from "@/lib/microcopy";
import { breakdownCategoryLabel, formatBreakdownLine, splitBreakdownLines } from "./format-pay-breakdown";

interface PayBreakdownPopoverProps {
  open: boolean;
  onClose: () => void;
  workerName: string;
  grossPay: number | null;
  equipmentCost: number;
  breakdown: PayBreakdownLine[];
  payWarnings: string[];
  canViewPayroll: boolean;
  canViewEquipmentCosts: boolean;
}

export function PayBreakdownPopover({
  open,
  onClose,
  workerName,
  grossPay,
  equipmentCost,
  breakdown,
  payWarnings,
  canViewPayroll,
  canViewEquipmentCosts,
}: PayBreakdownPopoverProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { labor, equipment } = splitBreakdownLines(breakdown);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-auto w-full max-w-md rounded-xl border-0 bg-card-white p-0 shadow-[var(--shadow-elevated)] backdrop:bg-navy-deep/50"
      onClose={onClose}
    >
      <div className="p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-navy-deep">
              <Receipt className="h-4 w-4" />
              Pay breakdown
            </h3>
            <p className="text-sm text-muted-foreground">{workerName}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 text-sm">
          {labor.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Worker gross pay
              </p>
              <ul className="space-y-1.5">
                {labor.map((line, index) => (
                  <li key={`${line.category}-${index}`} className="flex justify-between gap-3">
                    <span className="text-navy-deep">{formatBreakdownLine(line, canViewPayroll)}</span>
                    <span className="shrink-0 font-mono tabular-nums">
                      {canViewPayroll ? formatCurrency(line.amount) : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {equipment.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Site equipment cost
              </p>
              <ul className="space-y-1.5">
                {equipment.map((line, index) => (
                  <li key={`${line.category}-${index}`} className="flex justify-between gap-3">
                    <span className="text-navy-deep">
                      {breakdownCategoryLabel(line.category)}: {line.description},{" "}
                      {formatBreakdownLine(line, canViewEquipmentCosts).split(": ").slice(1).join(": ")}
                    </span>
                    <span className="shrink-0 font-mono tabular-nums">
                      {canViewEquipmentCosts ? formatCurrency(line.amount) : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {payWarnings.length > 0 ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {payWarnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
          ) : null}
        </div>

        <footer className="mt-5 space-y-1 border-t pt-4 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Gross pay</span>
            <span className="font-mono font-semibold">
              {canViewPayroll && grossPay != null ? formatCurrency(grossPay) : "—"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Equipment cost (site)</span>
            <span className="font-mono font-semibold">
              {canViewEquipmentCosts ? formatCurrency(equipmentCost) : "—"}
            </span>
          </div>
          {canViewEquipmentCosts ? (
            <FormHint className="pt-1">{MICROCOPY.equipmentCostSeparate}</FormHint>
          ) : null}
        </footer>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </dialog>
  );
}
